import { Component, ElementRef, HostListener, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Course, CourseSubscriptionSessionProvider } from '@stuplay';
import { BackUrlService } from '../../services/back-url.service';
import { StorageService } from '../../services/storage.service';
import { NavigationProvider } from '../../services/navigation.provider';

/**
 * Acciones que antes vivían en el topbar (ahora oculto): se muestran como un
 * botón de "más opciones" (⋮) a la derecha de cada heading de contenido del
 * player. A la izquierda mantiene la flecha para re-expandir el sidebar cuando
 * está colapsado (desktop) y el hamburger en mobile.
 *
 * Es autónomo: lee el curso desde StorageService, la sesión activa desde
 * NavigationProvider y el flag de preview desde los queryParams, así que basta
 * con envolver el <h2 class="heading"> de cada página.
 */
@Component({
    selector: 'msc-heading-actions',
    templateUrl: './heading-actions.component.html',
    styleUrls: ['./heading-actions.component.scss']
})
export class HeadingActionsComponent implements OnInit {
    // Cuando es false solo se muestra la flecha de re-expandir el sidebar
    // (y el hamburger en mobile), sin el menú de opciones (⋮). Útil en las
    // vistas de presentación/menú donde no aplican Resultados/Reset/Salir.
    @Input() showMenu: boolean = true;

    course: Course | any;
    preview: boolean = false;
    menuOpen: boolean = false;
    // Posición del menú (position: fixed) para no quedar recortado por
    // contenedores con overflow:hidden (p.ej. .column-left en exchange).
    menuPos: { top: number; right: number } = { top: 0, right: 0 };

    constructor(
        private el: ElementRef,
        private route: ActivatedRoute,
        private router: Router,
        private backUrlService: BackUrlService,
        private storageService: StorageService,
        private navProvider: NavigationProvider,
        private courseSubscriptionSessionProvider: CourseSubscriptionSessionProvider,
    ) { }

    ngOnInit(): void {
        this.course = this.storageService.get('course');
        this.route.queryParams.subscribe((params: Params) => {
            this.preview = !!params['preview'];
        });
    }

    toggleMenu(event: Event): void {
        event.stopPropagation();
        this.menuOpen = !this.menuOpen;
        if (this.menuOpen) {
            const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
            this.menuPos = {
                top: rect.bottom + 8,
                right: window.innerWidth - rect.right,
            };
        }
    }

    closeMenu(): void {
        this.menuOpen = false;
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (this.menuOpen && !this.el.nativeElement.contains(event.target)) {
            this.menuOpen = false;
        }
    }

    @HostListener('window:resize')
    @HostListener('window:scroll')
    onViewportChange(): void {
        this.menuOpen = false;
    }

    expandSidebar(): void {
        document.body.classList.remove('sidebar-collapsed');
    }

    toggleSidebar(): void {
        const layout = document.querySelector('.course-layout');
        if (layout) { layout.classList.toggle('sidebar-open'); }
    }

    back(): void {
        parent.postMessage('closeIframePlayer', '*');
        document.location.replace(this.backUrlService.getBackUrl());
    }

    resetCurrentSubscriptionSession(): void {
        const session = this.navProvider?.currentCourseSubscriptionSession;
        this.courseSubscriptionSessionProvider.resetCourseSubscriptionSession(session?.id).subscribe(() => {
            this.router.navigate(['course', this.course?.id], { queryParams: { preview: this.preview } });
            window.location.reload();
        });
    }
}
